import React from 'react'
import PropTypes from 'prop-types'
import createFocusTrap from 'focus-trap'

export const useFocusTrap = ({ initialFocus }) => {
  const containerRef = React.useRef()
  const focusTrapRef = React.useRef()

  React.useEffect(() => {
    if (!focusTrapRef.current && containerRef.current) {
      focusTrapRef.current = createFocusTrap(containerRef.current, {
        fallbackFocus: containerRef.current,
        clickOutsideDeactivates: true,
        initialFocus
      })
    }
  }, [initialFocus])

  const focusTrap = React.useMemo(() => {
    const activate = () => focusTrapRef.current && focusTrapRef.current.activate()
    const deactivate = () => focusTrapRef.current && focusTrapRef.current.deactivate()
    const pause = () => focusTrapRef.current && focusTrapRef.current.pause()
    const unpause = () => focusTrapRef.current && focusTrapRef.current.unpause()
    return { activate, deactivate, pause, unpause }
  }, [])

  return [containerRef, focusTrap]
}

const FocusTrap = ({ children, disabled, ...props }) => {
  const [containerRef, focusTrap] = useFocusTrap(props)

  React.useEffect(() => {
    if (disabled) return
    try {
      // NOTE: focus-trap will throw error if it can't find initialFocus node.
      focusTrap.activate()
      return focusTrap.deactivate
    } catch (err) {
      console.error(err)
    }
  }, [disabled, focusTrap])

  return (
    <div ref={containerRef} tabIndex='-1'>
      {children}
    </div>
  )
}

FocusTrap.propTypes = {
  disabled: PropTypes.bool
}

export default FocusTrap
